import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { FikoytKok3DElement } from '../fikoyt-kok-3d-element';
import { FikoSahne } from '../../FikoSahne';

// Global TWEEN referansı
declare var TWEEN: any;

// ============================================================================
// TİPLER VE ARAYÜZLER
// ============================================================================

/** Spline tipi */
export type SplineTipi = 'catmull-rom' | 'bezier' | 'bezier-cubic' | 'bezier-quadratic';

/** Çizgi stili */
export type SplineStili = 'duz' | 'kesikli' | 'noktali' | 'kesikli-noktali';

/** Çizgi render tipi */
export type SplineCizgiTipi = 'Line2' | 'Line' | 'LineSegments';

/** Catmull-Rom tension tipi (Three.js CurveType) */
export type CatmullRomTensionTipi = 'centripetal' | 'chordal' | 'catmullrom';

/** Spline ayarları */
export interface ISplineAyarlari {
    renk?: number | string;
    kalinlik?: number;
    stil?: SplineStili;
    kesikliAralik?: number;
    kesikliBoyut?: number;
    opaklık?: number;
    secilebilir?: boolean;
    katman?: number;
    cizgiTipi?: SplineCizgiTipi;
    enUstte?: boolean;
    renderEt?: boolean;
    kapali?: boolean;
    noktaGoster?: boolean;
    noktaBoyutu?: number;
    noktaRengi?: number | string;
    // Spline-spesifik ayarlar
    splineTipi?: SplineTipi;
    segmentSayisi?: number;  // Eğri interpolasyon çözünürlüğü
    tension?: number;  // Catmull-Rom tension (0-1)
    catmullTipi?: CatmullRomTensionTipi;
    kontolNoktaGoster?: boolean;  // Bezier kontrol noktalarını göster
    kontrolCizgiGoster?: boolean;  // Bezier kontrol çizgilerini göster
}

/** Varsayılan spline ayarları */
const VARSAYILAN_AYARLAR: ISplineAyarlari = {
    renk: 0xff6b00,  // Turuncu - Spline için farklı renk
    kalinlik: 2,
    stil: 'duz',
    kesikliAralik: 5,
    kesikliBoyut: 10,
    opaklık: 1,
    secilebilir: true,
    katman: 0,
    cizgiTipi: 'Line2',
    enUstte: false,
    renderEt: true,
    kapali: false,
    noktaGoster: true,
    noktaBoyutu: 6,
    noktaRengi: 0x00ff00,  // Yeşil kontrol noktaları
    splineTipi: 'catmull-rom',
    segmentSayisi: 50,
    tension: 0.5,
    catmullTipi: 'centripetal',
    kontolNoktaGoster: true,
    kontrolCizgiGoster: true
};

// ============================================================================
// HANDLE SİSTEMİ (Spline Control Point Handles)
// ============================================================================

/**
 * Spline kontrol noktaları için sürüklenebilir handle'lar
 * THREE.Points kullanarak zoom'dan bağımsız sabit boyutlu noktalar
 */
export class FikoYT3dSplineHandles extends THREE.Group {
    /** Bağlı spline referansı */
    private spline: FikoYT3dSpline;

    /** Points objesi - tüm handle noktaları */
    private _points: THREE.Points | null = null;

    /** Points geometrisi */
    private _geometry: THREE.BufferGeometry | null = null;

    /** Points materyali */
    private _material: THREE.PointsMaterial | null = null;

    /** Bezier kontrol noktaları için ayrı points */
    private _bezierPoints: THREE.Points | null = null;
    private _bezierGeometry: THREE.BufferGeometry | null = null;
    private _bezierMaterial: THREE.PointsMaterial | null = null;

    /** Bezier kontrol çizgileri */
    private _bezierLines: THREE.LineSegments | null = null;

    /** Sürükleme durumu */
    isDragging: boolean = false;

    /** Hangi handle sürükleniyor (index) */
    dragTarget: number | null = null;

    /** Bezier kontrol noktası mı sürükleniyor */
    dragBezierControl: boolean = false;

    /** Handle boyutu (pixel) */
    private handleBoyut: number = 14;

    /** Bezier kontrol handle boyutu */
    private bezierHandleBoyut: number = 10;

    /** Hit radius for raycast (world units) */
    private hitRadius: number = 10;

    /** Hover edilen handle index */
    private _hoverIndex: number = -1;

    // Pre-allocated buffers for hot-path operations
    private _tmpSphere = new THREE.Sphere();
    private _tmpIntersect = new THREE.Vector3();
    private _tmpScreenVec = new THREE.Vector3();

    /** Değişiklik callback'i */
    onChange?: (noktalar: THREE.Vector3[], bezierKontrolNoktalar?: THREE.Vector3[][]) => void;

    /** Eski API uyumluluğu için boş array */
    private _handleler: THREE.Object3D[] = [];

    constructor(spline: FikoYT3dSpline) {
        super();
        this.spline = spline;
        this.name = 'SplineHandles_' + spline.uuid;

        // Handle boyutunu kalınlığa göre ayarla
        this.handleBoyut = Math.max(12, Math.min(18, spline.kalinlik * 3));
        this.hitRadius = Math.max(6, spline.kalinlik * 2.5);

        // Points oluştur
        this.pointsOlustur();

        // Render order - her zaman üstte
        this.renderOrder = 1000;
    }

    /** Handle getter - eski API uyumluluğu */
    get handleler(): THREE.Object3D[] { return this._handleler; }

    /**
     * Points objesini oluştur
     */
    private pointsOlustur(): void {
        // Mevcut points'i temizle
        this.handleTemizle();

        // Internal array'e dogrudan eris - clone overhead'i yok
        const noktalar = this.spline['_kontrolNoktalar'];
        const noktaSayisi = noktalar.length;

        if (noktaSayisi === 0) return;

        // Ana kontrol noktaları için geometry oluştur
        this._geometry = new THREE.BufferGeometry();

        // Pozisyonlar
        const positions = new Float32Array(noktaSayisi * 3);
        for (let i = 0; i < noktaSayisi; i++) {
            positions[i * 3] = noktalar[i].x;
            positions[i * 3 + 1] = noktalar[i].y;
            positions[i * 3 + 2] = noktalar[i].z;
        }
        this._geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        // Renkler - başlangıç yeşil, bitiş kırmızı, ara turuncu
        const colors = new Float32Array(noktaSayisi * 3);
        for (let i = 0; i < noktaSayisi; i++) {
            let r: number, g: number, b: number;
            if (i === 0) {
                // Yeşil - başlangıç
                r = 0x2e / 255; g = 0xcc / 255; b = 0x71 / 255;
            } else if (i === noktaSayisi - 1 && !this.spline.kapali) {
                // Kırmızı - bitiş
                r = 0xe7 / 255; g = 0x4c / 255; b = 0x3c / 255;
            } else {
                // Turuncu - ara noktalar
                r = 0xff / 255; g = 0x9f / 255; b = 0x43 / 255;
            }
            colors[i * 3] = r;
            colors[i * 3 + 1] = g;
            colors[i * 3 + 2] = b;
        }
        this._geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        // Material - sizeAttenuation: false ile zoom'dan bağımsız
        this._material = new THREE.PointsMaterial({
            size: this.handleBoyut,
            sizeAttenuation: false,
            vertexColors: true,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity: 0.95
        });

        // Points objesi
        this._points = new THREE.Points(this._geometry, this._material);
        this._points.name = 'SplineHandlePoints';
        this._points.frustumCulled = false;
        this._points.renderOrder = 1002;
        this._points.layers.enableAll();

        // Spline referansı ekle
        this._points.userData['splineHandles'] = this;
        this._points.userData['secilebilir'] = true;

        // Çift tıklama delegasyonu
        const splineRef = this.spline;
        (this._points as any).ciftTiklandi = () => {
            if (typeof splineRef?.ciftTiklandi === 'function') {
                splineRef.ciftTiklandi();
            }
        };

        this.add(this._points);

        // Bezier kontrol noktaları ve çizgilerini oluştur
        if (this.spline.splineTipi.startsWith('bezier')) {
            this.bezierKontrolOlustur();
        }

        this.updateMatrixWorld(true);
    }

    /**
     * Bezier kontrol noktaları ve çizgilerini oluştur
     */
    private bezierKontrolOlustur(): void {
        // Internal array'e dogrudan eris - clone overhead'i yok
        const bezierNoktalar = this.spline['_bezierKontrolNoktalar'];
        if (!bezierNoktalar || bezierNoktalar.length === 0) return;

        if (!this.spline.kontrolNoktaGoster) return;

        // Tüm bezier kontrol noktalarını düz listeye çevir
        const tumBezierNoktalar: THREE.Vector3[] = [];
        bezierNoktalar.forEach(segmentNoktalar => {
            segmentNoktalar.forEach(nokta => tumBezierNoktalar.push(nokta));
        });

        if (tumBezierNoktalar.length === 0) return;

        // Bezier kontrol noktaları geometry
        this._bezierGeometry = new THREE.BufferGeometry();
        const bezierPositions = new Float32Array(tumBezierNoktalar.length * 3);
        for (let i = 0; i < tumBezierNoktalar.length; i++) {
            bezierPositions[i * 3] = tumBezierNoktalar[i].x;
            bezierPositions[i * 3 + 1] = tumBezierNoktalar[i].y;
            bezierPositions[i * 3 + 2] = tumBezierNoktalar[i].z;
        }
        this._bezierGeometry.setAttribute('position', new THREE.BufferAttribute(bezierPositions, 3));

        // Bezier kontrol noktaları material - mavi renk
        this._bezierMaterial = new THREE.PointsMaterial({
            size: this.bezierHandleBoyut,
            sizeAttenuation: false,
            color: 0x3498db,  // Mavi
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity: 0.8
        });

        this._bezierPoints = new THREE.Points(this._bezierGeometry, this._bezierMaterial);
        this._bezierPoints.name = 'SplineBezierControlPoints';
        this._bezierPoints.frustumCulled = false;
        this._bezierPoints.renderOrder = 1001;
        this._bezierPoints.userData['splineHandles'] = this;
        this._bezierPoints.userData['bezierKontrol'] = true;

        this.add(this._bezierPoints);

        // Bezier kontrol çizgileri
        if (this.spline.kontrolCizgiGoster) {
            this.bezierCizgiOlustur(bezierNoktalar);
        }
    }

    /**
     * Bezier kontrol çizgilerini oluştur
     */
    private bezierCizgiOlustur(bezierNoktalar: THREE.Vector3[][]): void {
        // Internal array'e dogrudan eris - clone overhead'i yok
        const anaNoktalar = this.spline['_kontrolNoktalar'];
        const cizgiNoktalar: number[] = [];

        // Her segment için kontrol çizgileri
        for (let i = 0; i < bezierNoktalar.length; i++) {
            const segmentKontrol = bezierNoktalar[i];
            const p0 = anaNoktalar[i];
            const p3 = anaNoktalar[(i + 1) % anaNoktalar.length];

            if (segmentKontrol.length >= 2) {
                // P0 -> C1 çizgisi
                cizgiNoktalar.push(p0.x, p0.y, p0.z);
                cizgiNoktalar.push(segmentKontrol[0].x, segmentKontrol[0].y, segmentKontrol[0].z);

                // C2 -> P3 çizgisi
                cizgiNoktalar.push(segmentKontrol[1].x, segmentKontrol[1].y, segmentKontrol[1].z);
                cizgiNoktalar.push(p3.x, p3.y, p3.z);
            }
        }

        if (cizgiNoktalar.length > 0) {
            const cizgiGeometry = new THREE.BufferGeometry();
            cizgiGeometry.setAttribute('position', new THREE.Float32BufferAttribute(cizgiNoktalar, 3));

            const cizgiMaterial = new THREE.LineBasicMaterial({
                color: 0x3498db,
                transparent: true,
                opacity: 0.4,
                depthTest: false,
                depthWrite: false
            });

            this._bezierLines = new THREE.LineSegments(cizgiGeometry, cizgiMaterial);
            this._bezierLines.name = 'SplineBezierControlLines';
            this._bezierLines.renderOrder = 1000;

            this.add(this._bezierLines);
        }
    }

    /**
     * Handle pozisyonlarını güncelle
     */
    guncelle(): void {
        if (!this.spline) return;

        // Internal array'e dogrudan eris - clone overhead'i yok
        const noktalar = this.spline['_kontrolNoktalar'];

        // Nokta sayısı değiştiyse yeniden oluştur
        if (!this._geometry || !this._points) {
            this.pointsOlustur();
            return;
        }

        const posAttr = this._geometry.getAttribute('position') as THREE.BufferAttribute;
        if (posAttr.count !== noktalar.length) {
            this.pointsOlustur();
            return;
        }

        // Pozisyonları güncelle
        for (let i = 0; i < noktalar.length; i++) {
            posAttr.setXYZ(i, noktalar[i].x, noktalar[i].y, noktalar[i].z);
        }
        posAttr.needsUpdate = true;

        // Renkleri güncelle
        const colorAttr = this._geometry.getAttribute('color') as THREE.BufferAttribute;
        for (let i = 0; i < noktalar.length; i++) {
            let r: number, g: number, b: number;
            if (i === 0) {
                r = 0x2e / 255; g = 0xcc / 255; b = 0x71 / 255;
            } else if (i === noktalar.length - 1 && !this.spline.kapali) {
                r = 0xe7 / 255; g = 0x4c / 255; b = 0x3c / 255;
            } else {
                r = 0xff / 255; g = 0x9f / 255; b = 0x43 / 255;
            }
            colorAttr.setXYZ(i, r, g, b);
        }
        colorAttr.needsUpdate = true;

        // Bezier kontrol noktalarını güncelle
        if (this.spline.splineTipi.startsWith('bezier')) {
            this.bezierKontrolGuncelle();
        }

        this.updateMatrixWorld(true);
    }

    /**
     * Bezier kontrol noktalarını güncelle
     */
    private bezierKontrolGuncelle(): void {
        // Eski bezier elemanlarını temizle
        if (this._bezierPoints) {
            this.remove(this._bezierPoints);
            this._bezierGeometry?.dispose();
            this._bezierMaterial?.dispose();
            this._bezierPoints = null;
        }
        if (this._bezierLines) {
            this.remove(this._bezierLines);
            this._bezierLines.geometry.dispose();
            (this._bezierLines.material as THREE.Material).dispose();
            this._bezierLines = null;
        }

        // Yeniden oluştur
        if (this.spline.splineTipi.startsWith('bezier')) {
            this.bezierKontrolOlustur();
        }
    }

    /**
     * Hover efekti uygula
     */
    setHover(handle: THREE.Object3D | null): void {
        if (handle && handle.userData['handleIndex'] !== undefined) {
            this._hoverIndex = handle.userData['handleIndex'];
        } else {
            this._hoverIndex = -1;
        }
    }

    /**
     * Hover edilen handle index'ini ayarla
     */
    setHoverIndex(index: number): void {
        this._hoverIndex = index;
    }

    /**
     * Raycast için targets döndür
     */
    getRaycastTargets(): THREE.Object3D[] {
        const targets: THREE.Object3D[] = [];
        if (this._points) targets.push(this._points);
        if (this._bezierPoints) targets.push(this._bezierPoints);
        return targets;
    }

    /**
     * Manuel küre kesişim testi
     */
    manuelKureKesisim(ray: THREE.Ray): { hit: boolean; handleIndex: number | null; mesafe: number; bezierKontrol?: boolean } {
        this.updateMatrixWorld(true);

        let enYakinMesafe = Infinity;
        let bulunanIndex: number | null = null;
        let bezierKontrol = false;

        // Ana kontrol noktaları - internal array'e dogrudan eris (clone yok)
        const noktalar = this.spline['_kontrolNoktalar'];
        const sphere = this._tmpSphere;
        const intersect = this._tmpIntersect;

        for (let i = 0; i < noktalar.length; i++) {
            sphere.set(noktalar[i], this.hitRadius);

            if (ray.intersectSphere(sphere, intersect)) {
                const mesafe = ray.origin.distanceTo(intersect);
                if (mesafe < enYakinMesafe) {
                    enYakinMesafe = mesafe;
                    bulunanIndex = i;
                    bezierKontrol = false;
                }
            }
        }

        // Bezier kontrol noktaları
        if (this.spline.splineTipi.startsWith('bezier') && this.spline.kontrolNoktaGoster) {
            const bezierNoktalar = this.spline['_bezierKontrolNoktalar'];
            let bezierIndex = 0;
            for (let seg = 0; seg < bezierNoktalar.length; seg++) {
                for (let ctrl = 0; ctrl < bezierNoktalar[seg].length; ctrl++) {
                    sphere.set(bezierNoktalar[seg][ctrl], this.hitRadius * 0.8);

                    if (ray.intersectSphere(sphere, intersect)) {
                        const mesafe = ray.origin.distanceTo(intersect);
                        if (mesafe < enYakinMesafe) {
                            enYakinMesafe = mesafe;
                            bulunanIndex = bezierIndex;
                            bezierKontrol = true;
                        }
                    }
                    bezierIndex++;
                }
            }
        }

        return {
            hit: bulunanIndex !== null,
            handleIndex: bulunanIndex,
            mesafe: enYakinMesafe,
            bezierKontrol
        };
    }

    /**
     * Ekran koordinatlarına göre en yakın handle'ı bul
     */
    noktadanHandleBul(
        screenX: number,
        screenY: number,
        kamera: THREE.Camera,
        threshold: number = 25
    ): { index: number; mesafe: number; bezierKontrol?: boolean } | null {
        // Internal array'e dogrudan eris (clone yok)
        const noktalar = this.spline['_kontrolNoktalar'];
        let enYakinIndex = -1;
        let enYakinMesafe = Infinity;
        let bezierKontrol = false;

        const tempVec = this._tmpScreenVec;

        // Ana kontrol noktaları
        for (let i = 0; i < noktalar.length; i++) {
            tempVec.copy(noktalar[i]);
            tempVec.project(kamera);

            const dx = (tempVec.x - screenX) * 0.5;
            const dy = (tempVec.y - screenY) * 0.5;
            const screenDist = Math.sqrt(dx * dx + dy * dy);

            if (screenDist < enYakinMesafe) {
                enYakinMesafe = screenDist;
                enYakinIndex = i;
                bezierKontrol = false;
            }
        }

        const normalizedThreshold = threshold / 500;

        if (enYakinIndex >= 0 && enYakinMesafe < normalizedThreshold) {
            return { index: enYakinIndex, mesafe: enYakinMesafe, bezierKontrol };
        }

        return null;
    }

    /**
     * Handle'ları temizle
     */
    private handleTemizle(): void {
        if (this._points) {
            this.remove(this._points);
        }
        if (this._geometry) {
            this._geometry.dispose();
            this._geometry = null;
        }
        if (this._material) {
            this._material.dispose();
            this._material = null;
        }
        this._points = null;

        // Bezier elemanlarını temizle
        if (this._bezierPoints) {
            this.remove(this._bezierPoints);
            this._bezierGeometry?.dispose();
            this._bezierMaterial?.dispose();
            this._bezierPoints = null;
        }
        if (this._bezierLines) {
            this.remove(this._bezierLines);
            this._bezierLines.geometry.dispose();
            (this._bezierLines.material as THREE.Material).dispose();
            this._bezierLines = null;
        }

        this._handleler = [];
    }

    /**
     * Kaynakları temizle
     */
    temizle(): void {
        this.handleTemizle();

        if (this.parent) {
            this.parent.remove(this);
        }
    }
}

// ============================================================================
// ANA SINIF
// ============================================================================

/**
 * FikoYT 3D Spline Sınıfı
 *
 * Kontrol noktalarıyla tanımlanan yumuşak eğri.
 * Catmull-Rom ve Bezier interpolasyon destekler.
 * İsteğe bağlı olarak kapalı (loop) yapılabilir.
 *
 * @extends FikoytKok3DElement
 */
export class FikoYT3dSpline extends FikoytKok3DElement {
    /** Otomatik isimlendirme için sayaç */
    static SAYAC = 1;

    override type = 'FikoYT3dSpline';

    // ========================================================================
    // ÖZELLİKLER
    // ========================================================================

    /** Kontrol noktaları */
    private _kontrolNoktalar: THREE.Vector3[] = [];

    /** Bezier kontrol noktaları (her segment için 2 kontrol noktası) */
    private _bezierKontrolNoktalar: THREE.Vector3[][] = [];

    /** Spline ayarları */
    private _ayarlar: ISplineAyarlari = { ...VARSAYILAN_AYARLAR };

    /** Three.js çizgi nesnesi */
    private _cizgi: Line2 | THREE.Line | THREE.LineSegments | null = null;

    /** Çizgi geometrisi */
    private _geometri: LineGeometry | THREE.BufferGeometry | null = null;

    /** Çizgi materyali */
    private _materyal: LineMaterial | THREE.LineBasicMaterial | THREE.LineDashedMaterial | null = null;

    /** Three.js Curve nesnesi */
    private _curve: THREE.Curve<THREE.Vector3> | null = null;

    /** Sahne referansı */
    sahne?: FikoSahne;

    /** Handle'lar */
    private _handles: FikoYT3dSplineHandles | null = null;

    /** Sağ tıklama menüsü */
    public sagMenu: any[] = [];

    // Pre-allocated buffers for performance-critical operations
    private _tmpVec3A = new THREE.Vector3();
    private _tmpVec3B = new THREE.Vector3();
    private _tmpVec3C = new THREE.Vector3();
    private _tmpVec3D = new THREE.Vector3();
    private _tmpVec2 = new THREE.Vector2();

    // ========================================================================
    // GETTER / SETTER
    // ========================================================================

    /** Kontrol noktaları (kopya döndürür) */
    get kontrolNoktalar(): THREE.Vector3[] {
        return this._kontrolNoktalar.map(n => n.clone());
    }
    set kontrolNoktalar(deger: THREE.Vector3[]) {
        this._kontrolNoktalar = deger.map(n => n.clone());
        this.bezierKontrolNoktalariniHesapla();
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /** Bezier kontrol noktaları */
    get bezierKontrolNoktalar(): THREE.Vector3[][] {
        return this._bezierKontrolNoktalar.map(seg => seg.map(n => n.clone()));
    }
    set bezierKontrolNoktalar(deger: THREE.Vector3[][]) {
        this._bezierKontrolNoktalar = deger.map(seg => seg.map(n => n.clone()));
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /** Nokta sayısı */
    get noktaSayisi(): number {
        return this._kontrolNoktalar.length;
    }

    /** Toplam eğri uzunluğu */
    get toplamUzunluk(): number {
        if (!this._curve) return 0;
        return this._curve.getLength();
    }

    /** Ağırlık merkezi - pre-allocated result (callers should .clone() if they store it) */
    get merkezNokta(): THREE.Vector3 {
        const merkez = this._tmpVec3D;
        merkez.set(0, 0, 0);
        if (this._kontrolNoktalar.length === 0) return merkez;
        for (let i = 0; i < this._kontrolNoktalar.length; i++) {
            merkez.add(this._kontrolNoktalar[i]);
        }
        merkez.divideScalar(this._kontrolNoktalar.length);
        return merkez;
    }

    // Pre-allocated Box3 for boundingBox getter
    private _tmpBox3 = new THREE.Box3();

    /** Bounding Box - pre-allocated result (callers should .clone() if they store it) */
    get boundingBox(): THREE.Box3 {
        this._tmpBox3.makeEmpty();
        for (let i = 0; i < this._kontrolNoktalar.length; i++) {
            this._tmpBox3.expandByPoint(this._kontrolNoktalar[i]);
        }
        return this._tmpBox3;
    }

    /** Kapalı mı */
    get kapali(): boolean { return this._ayarlar.kapali!; }
    set kapali(deger: boolean) {
        this._ayarlar.kapali = deger;
        this.bezierKontrolNoktalariniHesapla();
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /** Spline tipi */
    get splineTipi(): SplineTipi { return this._ayarlar.splineTipi!; }
    set splineTipi(deger: SplineTipi) {
        this._ayarlar.splineTipi = deger;
        this.bezierKontrolNoktalariniHesapla();
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /** Segment sayısı (interpolasyon çözünürlüğü) */
    get segmentSayisi(): number { return this._ayarlar.segmentSayisi!; }
    set segmentSayisi(deger: number) {
        this._ayarlar.segmentSayisi = Math.max(10, Math.min(200, deger));
        this.cizgiyiGuncelle();
    }

    /** Catmull-Rom tension */
    get tension(): number { return this._ayarlar.tension!; }
    set tension(deger: number) {
        this._ayarlar.tension = Math.max(0, Math.min(1, deger));
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /** Catmull-Rom tipi */
    get catmullTipi(): CatmullRomTensionTipi { return this._ayarlar.catmullTipi!; }
    set catmullTipi(deger: CatmullRomTensionTipi) {
        this._ayarlar.catmullTipi = deger;
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /** Kontrol noktalarını göster */
    get kontrolNoktaGoster(): boolean { return this._ayarlar.kontolNoktaGoster!; }
    set kontrolNoktaGoster(deger: boolean) {
        this._ayarlar.kontolNoktaGoster = deger;
        if (this._handles) {
            this._handles.guncelle();
        }
    }

    /** Kontrol çizgilerini göster */
    get kontrolCizgiGoster(): boolean { return this._ayarlar.kontrolCizgiGoster!; }
    set kontrolCizgiGoster(deger: boolean) {
        this._ayarlar.kontrolCizgiGoster = deger;
        if (this._handles) {
            this._handles.guncelle();
        }
    }

    /** Renk */
    get renk(): number | string { return this._ayarlar.renk!; }
    set renk(deger: number | string) {
        this._ayarlar.renk = deger;
        this.materyaliGuncelle();
    }

    /** Kalınlık */
    get kalinlik(): number { return this._ayarlar.kalinlik!; }
    set kalinlik(deger: number) {
        this._ayarlar.kalinlik = deger;
        this.materyaliGuncelle();
    }

    /** Çizgi stili */
    get stil(): SplineStili { return this._ayarlar.stil!; }
    set stil(deger: SplineStili) {
        this._ayarlar.stil = deger;
        this.stilGuncelle();
    }

    /** Opaklık */
    get opaklık(): number { return this._ayarlar.opaklık!; }
    set opaklık(deger: number) {
        this._ayarlar.opaklık = Math.max(0, Math.min(1, deger));
        this.materyaliGuncelle();
    }

    /** Seçilebilirlik */
    get secilebilir(): boolean { return this._ayarlar.secilebilir!; }
    set secilebilir(deger: boolean) {
        this._ayarlar.secilebilir = deger;
        if (this._cizgi) {
            this._cizgi.userData['secilebilir'] = deger;
        }
    }

    /** Çizgi tipi */
    get cizgiTipi(): SplineCizgiTipi { return this._ayarlar.cizgiTipi || 'Line2'; }
    set cizgiTipi(deger: SplineCizgiTipi) {
        if (this._ayarlar.cizgiTipi === deger) return;
        this._ayarlar.cizgiTipi = deger;
        this.cizgiyiYenidenOlustur();
    }

    /** Nokta göster */
    get noktaGoster(): boolean { return this._ayarlar.noktaGoster!; }
    set noktaGoster(deger: boolean) {
        this._ayarlar.noktaGoster = deger;
    }

    /** En üstte */
    get enUstte(): boolean { return this._ayarlar.enUstte!; }
    set enUstte(deger: boolean) {
        this._ayarlar.enUstte = deger;
        this.depthAyarla();
    }

    /** Render et */
    get renderEt(): boolean { return this._ayarlar.renderEt!; }
    set renderEt(deger: boolean) {
        this._ayarlar.renderEt = deger;
        this.visible = deger;
    }

    // ========================================================================
    // CONSTRUCTOR
    // ========================================================================

    constructor(sahne?: FikoSahne, isim?: string) {
        if (!isim || isim.trim() === '') {
            isim = `Spline.${FikoYT3dSpline.SAYAC++}`;
        }
        super(sahne, isim);

        this.simge = '/simgeler/line.webp';
        this.userData['FikoArayuz'].elementTip = 'Spline';
        this.etiket = this.name;

        // Varsayılan 4 nokta ile başla (S şekli)
        this._kontrolNoktalar = [
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(30, 40, 0),
            new THREE.Vector3(70, -20, 0),
            new THREE.Vector3(100, 30, 0)
        ];

        // Bezier kontrol noktalarını hesapla
        this.bezierKontrolNoktalariniHesapla();

        // Eğriyi oluştur
        this.egriOlustur();

        // Çizgiyi oluştur
        this.cizgiyiOlustur();

        // Sağ tıklama menüsü
        this.sagMenuOlustur();
    }

    // ========================================================================
    // EĞRİ OLUŞTURMA
    // ========================================================================

    /**
     * Bezier kontrol noktalarını otomatik hesapla
     * (Smooth Bezier için)
     */
    private bezierKontrolNoktalariniHesapla(): void {
        if (!this._ayarlar.splineTipi?.startsWith('bezier')) return;
        if (this._kontrolNoktalar.length < 2) {
            this._bezierKontrolNoktalar = [];
            return;
        }

        this._bezierKontrolNoktalar = [];
        const n = this._kontrolNoktalar.length;
        const kapali = this._ayarlar.kapali;

        // Pre-allocated vectors for loop
        const c1Calc = this._tmpVec3A;
        const c2Calc = this._tmpVec3B;

        for (let i = 0; i < (kapali ? n : n - 1); i++) {
            const p0 = this._kontrolNoktalar[i];
            const p1 = this._kontrolNoktalar[(i + 1) % n];
            const pPrev = this._kontrolNoktalar[(i - 1 + n) % n];
            const pNext = this._kontrolNoktalar[(i + 2) % n];

            // Smooth bezier için kontrol noktaları hesapla
            const smoothFactor = 0.25;

            // C1: P0'dan P1'e doğru, önceki noktanın etkisiyle
            if (i > 0 || kapali) {
                c1Calc.copy(p1).sub(pPrev).multiplyScalar(smoothFactor).add(p0);
            } else {
                c1Calc.copy(p1).sub(p0).multiplyScalar(smoothFactor).add(p0);
            }
            const c1 = c1Calc.clone();

            // C2: P1'den P0'a doğru, sonraki noktanın etkisiyle
            if (i < n - 2 || kapali) {
                c2Calc.copy(p0).sub(pNext).multiplyScalar(smoothFactor).add(p1);
            } else {
                c2Calc.copy(p0).sub(p1).multiplyScalar(smoothFactor).add(p1);
            }
            const c2 = c2Calc.clone();

            this._bezierKontrolNoktalar.push([c1, c2]);
        }
    }

    /**
     * Eğri oluştur (tip'e göre)
     */
    private egriOlustur(): void {
        if (this._kontrolNoktalar.length < 2) {
            this._curve = null;
            this.invalidateCurveCache();
            return;
        }

        const splineTipi = this._ayarlar.splineTipi || 'catmull-rom';

        switch (splineTipi) {
            case 'catmull-rom':
                this._curve = new THREE.CatmullRomCurve3(
                    this._kontrolNoktalar,
                    this._ayarlar.kapali,
                    this._ayarlar.catmullTipi as THREE.CurveType,
                    this._ayarlar.tension
                );
                break;

            case 'bezier':
            case 'bezier-cubic':
                this._curve = this.bezierCubicEgriOlustur();
                break;

            case 'bezier-quadratic':
                this._curve = this.bezierQuadraticEgriOlustur();
                break;

            default:
                this._curve = new THREE.CatmullRomCurve3(
                    this._kontrolNoktalar,
                    this._ayarlar.kapali,
                    'centripetal',
                    0.5
                );
        }
        // Yeni egri olusturuldu, cache gecersiz
        this.invalidateCurveCache();
    }

    /**
     * Cubic Bezier eğrisi oluştur
     */
    private bezierCubicEgriOlustur(): THREE.CurvePath<THREE.Vector3> {
        const curvePath = new THREE.CurvePath<THREE.Vector3>();
        const n = this._kontrolNoktalar.length;
        const kapali = this._ayarlar.kapali;

        // Pre-allocated vectors for default control points
        const mid = this._tmpVec3A;
        const c1Temp = this._tmpVec3B;
        const c2Temp = this._tmpVec3C;

        for (let i = 0; i < (kapali ? n : n - 1); i++) {
            const p0 = this._kontrolNoktalar[i];
            const p3 = this._kontrolNoktalar[(i + 1) % n];

            let c1: THREE.Vector3, c2: THREE.Vector3;

            if (this._bezierKontrolNoktalar[i] && this._bezierKontrolNoktalar[i].length >= 2) {
                c1 = this._bezierKontrolNoktalar[i][0];
                c2 = this._bezierKontrolNoktalar[i][1];
            } else {
                // Varsayılan kontrol noktaları - use pre-allocated buffers
                mid.addVectors(p0, p3).multiplyScalar(0.5);
                c1Temp.lerpVectors(p0, mid, 0.5);
                c2Temp.lerpVectors(mid, p3, 0.5);
                // Clone only once per segment when needed
                c1 = c1Temp.clone();
                c2 = c2Temp.clone();
            }

            const bezierCurve = new THREE.CubicBezierCurve3(p0, c1, c2, p3);
            curvePath.add(bezierCurve);
        }

        return curvePath;
    }

    /**
     * Quadratic Bezier eğrisi oluştur
     */
    private bezierQuadraticEgriOlustur(): THREE.CurvePath<THREE.Vector3> {
        const curvePath = new THREE.CurvePath<THREE.Vector3>();
        const n = this._kontrolNoktalar.length;
        const kapali = this._ayarlar.kapali;

        // Pre-allocated vectors for loop calculations
        const c1Calc = this._tmpVec3A;
        const normalCalc = this._tmpVec3B;
        const zAxis = this._tmpVec3C;
        zAxis.set(0, 0, 1);

        for (let i = 0; i < (kapali ? n : n - 1); i++) {
            const p0 = this._kontrolNoktalar[i];
            const p2 = this._kontrolNoktalar[(i + 1) % n];

            // Orta kontrol noktası - use pre-allocated buffer
            c1Calc.addVectors(p0, p2).multiplyScalar(0.5);
            // Biraz yukarı/yana kaydır
            normalCalc.subVectors(p2, p0).cross(zAxis).normalize();
            c1Calc.add(normalCalc.multiplyScalar(p0.distanceTo(p2) * 0.2));

            // Clone only once per segment
            const c1 = c1Calc.clone();
            const bezierCurve = new THREE.QuadraticBezierCurve3(p0, c1, p2);
            curvePath.add(bezierCurve);
        }

        return curvePath;
    }

    // ========================================================================
    // OLUŞTURMA METODLARİ
    // ========================================================================

    /**
     * Noktalardan spline oluştur
     */
    noktalarileBelirle(noktalar: THREE.Vector3[], kapali: boolean = false): void {
        if (noktalar.length < 2) {
            console.warn('[FikoYT3dSpline] En az 2 nokta gerekli');
            return;
        }

        this._kontrolNoktalar = noktalar.map(n => n.clone());
        this._ayarlar.kapali = kapali;
        this.bezierKontrolNoktalariniHesapla();
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /**
     * Düzenli dalga (sinüs) oluştur
     */
    sinusOlustur(baslangic: THREE.Vector3, bitis: THREE.Vector3, dalgaSayisi: number = 3, genlik: number = 20): void {
        const noktalar: THREE.Vector3[] = [];
        const noktaSayisi = dalgaSayisi * 4 + 1;
        const yonVec = this._tmpVec3A;
        yonVec.subVectors(bitis, baslangic);
        const uzunluk = yonVec.length();
        yonVec.normalize();

        // Dik vektör - pre-allocated buffer
        const dikVec = this._tmpVec3B;
        dikVec.set(-yonVec.y, yonVec.x, 0);

        // Pre-allocated offset buffer
        const offset = this._tmpVec3C;

        for (let i = 0; i < noktaSayisi; i++) {
            const t = i / (noktaSayisi - 1);
            const x = baslangic.x + yonVec.x * uzunluk * t;
            const y = baslangic.y + yonVec.y * uzunluk * t;
            const z = baslangic.z;

            // Sinüs dalgası - use pre-allocated buffer
            const sinVal = Math.sin(t * Math.PI * 2 * dalgaSayisi);
            offset.copy(dikVec).multiplyScalar(sinVal * genlik);

            noktalar.push(new THREE.Vector3(x + offset.x, y + offset.y, z));
        }

        this._kontrolNoktalar = noktalar;
        this._ayarlar.kapali = false;
        this.bezierKontrolNoktalariniHesapla();
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /**
     * Spiral oluştur
     */
    spiralOlustur(merkez: THREE.Vector3, baslangicYaricap: number, bitisYaricap: number, turSayisi: number = 3, yukseklik: number = 0): void {
        const noktalar: THREE.Vector3[] = [];
        const noktaSayisi = turSayisi * 12;

        for (let i = 0; i <= noktaSayisi; i++) {
            const t = i / noktaSayisi;
            const aci = t * Math.PI * 2 * turSayisi;
            const yaricap = baslangicYaricap + (bitisYaricap - baslangicYaricap) * t;
            const z = merkez.z + yukseklik * t;

            noktalar.push(new THREE.Vector3(
                merkez.x + Math.cos(aci) * yaricap,
                merkez.y + Math.sin(aci) * yaricap,
                z
            ));
        }

        this._kontrolNoktalar = noktalar;
        this._ayarlar.kapali = false;
        this.bezierKontrolNoktalariniHesapla();
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /**
     * Elips oluştur
     */
    elipsOlustur(merkez: THREE.Vector3, yaricapX: number, yaricapY: number, noktaSayisi: number = 12): void {
        const noktalar: THREE.Vector3[] = [];

        for (let i = 0; i < noktaSayisi; i++) {
            const aci = (i / noktaSayisi) * Math.PI * 2;
            noktalar.push(new THREE.Vector3(
                merkez.x + Math.cos(aci) * yaricapX,
                merkez.y + Math.sin(aci) * yaricapY,
                merkez.z
            ));
        }

        this._kontrolNoktalar = noktalar;
        this._ayarlar.kapali = true;
        this.bezierKontrolNoktalariniHesapla();
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    // ========================================================================
    // NOKTA YÖNETİMİ
    // ========================================================================

    /**
     * Kontrol noktası ekle (sona)
     */
    noktaEkle(nokta: THREE.Vector3): void {
        this._kontrolNoktalar.push(nokta.clone());
        this.bezierKontrolNoktalariniHesapla();
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /**
     * Belirli bir indekse kontrol noktası ekle
     */
    noktaEkleIndex(index: number, nokta: THREE.Vector3): void {
        if (index < 0 || index > this._kontrolNoktalar.length) {
            console.warn('[FikoYT3dSpline] Geçersiz index');
            return;
        }
        this._kontrolNoktalar.splice(index, 0, nokta.clone());
        this.bezierKontrolNoktalariniHesapla();
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /**
     * Kontrol noktası sil
     */
    noktaSil(index: number): void {
        if (index < 0 || index >= this._kontrolNoktalar.length) {
            console.warn('[FikoYT3dSpline] Geçersiz index');
            return;
        }
        if (this._kontrolNoktalar.length <= 2) {
            console.warn('[FikoYT3dSpline] En az 2 nokta kalmalı');
            return;
        }
        this._kontrolNoktalar.splice(index, 1);
        this.bezierKontrolNoktalariniHesapla();
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /**
     * Kontrol noktası pozisyonunu güncelle
     */
    noktaGuncelle(index: number, yeniPozisyon: THREE.Vector3): void {
        if (index < 0 || index >= this._kontrolNoktalar.length) {
            console.warn('[FikoYT3dSpline] Geçersiz index');
            return;
        }
        this._kontrolNoktalar[index].copy(yeniPozisyon);
        this.bezierKontrolNoktalariniHesapla();
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /**
     * Bezier kontrol noktasını güncelle
     */
    bezierNoktaGuncelle(segmentIndex: number, kontrolIndex: number, yeniPozisyon: THREE.Vector3): void {
        if (segmentIndex < 0 || segmentIndex >= this._bezierKontrolNoktalar.length) return;
        if (kontrolIndex < 0 || kontrolIndex >= this._bezierKontrolNoktalar[segmentIndex].length) return;

        this._bezierKontrolNoktalar[segmentIndex][kontrolIndex].copy(yeniPozisyon);
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /**
     * Eğri üzerinde bir noktaya yeni kontrol noktası ekle
     */
    egriUzerindeNoktaEkle(t: number): void {
        if (!this._curve || t < 0 || t > 1) return;

        const yeniNokta = this._curve.getPoint(t);

        // En yakın segment'i bul ve ona ekle
        let enYakinIndex = 0;
        let enYakinMesafe = Infinity;
        const segmentMerkez = this._tmpVec3A;

        for (let i = 0; i < this._kontrolNoktalar.length - 1; i++) {
            segmentMerkez.addVectors(this._kontrolNoktalar[i], this._kontrolNoktalar[i + 1])
                .multiplyScalar(0.5);
            const mesafe = yeniNokta.distanceTo(segmentMerkez);
            if (mesafe < enYakinMesafe) {
                enYakinMesafe = mesafe;
                enYakinIndex = i + 1;
            }
        }

        this.noktaEkleIndex(enYakinIndex, yeniNokta);
    }

    /**
     * Tüm noktaları taşı
     */
    tasi(delta: THREE.Vector3): void {
        // Direct in-place addition - no allocation needed
        for (let i = 0; i < this._kontrolNoktalar.length; i++) {
            this._kontrolNoktalar[i].add(delta);
        }
        for (let i = 0; i < this._bezierKontrolNoktalar.length; i++) {
            for (let j = 0; j < this._bezierKontrolNoktalar[i].length; j++) {
                this._bezierKontrolNoktalar[i][j].add(delta);
            }
        }
        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /**
     * Merkez etrafında döndür
     */
    dondur(aci: number, eksen: THREE.Vector3 = new THREE.Vector3(0, 0, 1)): void {
        const merkez = this._tmpVec3D;
        merkez.set(0, 0, 0);
        for (let i = 0; i < this._kontrolNoktalar.length; i++) {
            merkez.add(this._kontrolNoktalar[i]);
        }
        merkez.divideScalar(this._kontrolNoktalar.length);

        const eksenNorm = this._tmpVec3A;
        eksenNorm.copy(eksen).normalize();
        const quaternion = new THREE.Quaternion().setFromAxisAngle(eksenNorm, aci);

        // Rotate all points in-place
        for (let i = 0; i < this._kontrolNoktalar.length; i++) {
            this._kontrolNoktalar[i].sub(merkez);
            this._kontrolNoktalar[i].applyQuaternion(quaternion);
            this._kontrolNoktalar[i].add(merkez);
        }

        for (let i = 0; i < this._bezierKontrolNoktalar.length; i++) {
            for (let j = 0; j < this._bezierKontrolNoktalar[i].length; j++) {
                this._bezierKontrolNoktalar[i][j].sub(merkez);
                this._bezierKontrolNoktalar[i][j].applyQuaternion(quaternion);
                this._bezierKontrolNoktalar[i][j].add(merkez);
            }
        }

        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    /**
     * Ölçekle
     */
    olcekle(faktor: number, merkez?: THREE.Vector3): void {
        const pivot = this._tmpVec3A;
        if (merkez) {
            pivot.copy(merkez);
        } else {
            pivot.set(0, 0, 0);
            for (let i = 0; i < this._kontrolNoktalar.length; i++) {
                pivot.add(this._kontrolNoktalar[i]);
            }
            pivot.divideScalar(this._kontrolNoktalar.length);
        }

        // Scale all points in-place
        for (let i = 0; i < this._kontrolNoktalar.length; i++) {
            this._kontrolNoktalar[i].sub(pivot);
            this._kontrolNoktalar[i].multiplyScalar(faktor);
            this._kontrolNoktalar[i].add(pivot);
        }

        for (let i = 0; i < this._bezierKontrolNoktalar.length; i++) {
            for (let j = 0; j < this._bezierKontrolNoktalar[i].length; j++) {
                this._bezierKontrolNoktalar[i][j].sub(pivot);
                this._bezierKontrolNoktalar[i][j].multiplyScalar(faktor);
                this._bezierKontrolNoktalar[i][j].add(pivot);
            }
        }

        this.egriOlustur();
        this.cizgiyiGuncelle();
    }

    // ========================================================================
    // EĞRİ SORGULAMA
    // ========================================================================

    /**
     * Eğri üzerinde t parametresindeki noktayı al
     */
    noktaAl(t: number): THREE.Vector3 | null {
        if (!this._curve) return null;
        return this._curve.getPoint(Math.max(0, Math.min(1, t)));
    }

    /**
     * Eğri üzerinde t parametresindeki teğet vektörünü al
     */
    tegetAl(t: number): THREE.Vector3 | null {
        if (!this._curve) return null;
        return this._curve.getTangent(Math.max(0, Math.min(1, t)));
    }

    /**
     * Eğriyi eşit aralıklı noktalara böl
     */
    noktalarAl(sayisi: number): THREE.Vector3[] {
        if (!this._curve) return [];
        return this._curve.getPoints(sayisi);
    }

    /**
     * Eğri üzerinde en yakın noktayı bul
     */
    enYakinNoktaBul(hedefNokta: THREE.Vector3, aramaAdimi: number = 100): { nokta: THREE.Vector3; t: number; mesafe: number } | null {
        if (!this._curve) return null;

        let enYakinT = 0;
        let enYakinMesafe = Infinity;

        // Pre-allocated buffer for point sampling
        const noktaBuffer = this._tmpVec3A;

        for (let i = 0; i <= aramaAdimi; i++) {
            const t = i / aramaAdimi;
            this._curve.getPoint(t, noktaBuffer);
            const mesafe = noktaBuffer.distanceTo(hedefNokta);

            if (mesafe < enYakinMesafe) {
                enYakinMesafe = mesafe;
                enYakinT = t;
            }
        }

        return {
            nokta: this._curve.getPoint(enYakinT),
            t: enYakinT,
            mesafe: enYakinMesafe
        };
    }

    // ========================================================================
    // ÇİZGİ OLUŞTURMA
    // ========================================================================

    /**
     * Çizgiyi oluştur
     */
    private cizgiyiOlustur(): void {
        if (!this._curve) return;

        // Geometri oluştur
        this.geometriOlustur();

        // Materyal oluştur
        this.materyalOlustur();

        // Çizgi mesh'i oluştur
        this.cizgiMeshiOlustur();
    }

    /**
     * Geometri oluştur
     */
    private geometriOlustur(): void {
        if (!this._curve) return;

        const noktalar = this.getCachedCurvePoints();

        if (this._ayarlar.cizgiTipi === 'Line2') {
            const positions: number[] = [];
            for (let i = 0; i < noktalar.length; i++) {
                positions.push(noktalar[i].x, noktalar[i].y, noktalar[i].z);
            }

            this._geometri = new LineGeometry();
            (this._geometri as LineGeometry).setPositions(positions);
        } else {
            this._geometri = new THREE.BufferGeometry().setFromPoints(noktalar);
        }
    }

    /**
     * Materyal oluştur
     */
    private materyalOlustur(): void {
        const renkDeger = typeof this._ayarlar.renk === 'string'
            ? new THREE.Color(this._ayarlar.renk).getHex()
            : this._ayarlar.renk!;

        if (this._ayarlar.cizgiTipi === 'Line2') {
            let resolutionX = 1920;
            let resolutionY = 1080;

            try {
                if (this.sahne?.anaGosterim?.renderer) {
                    const renderer = this.sahne.anaGosterim.renderer as unknown as THREE.WebGLRenderer;
                    if (renderer.getSize) {
                        renderer.getSize(this._tmpVec2);
                        resolutionX = this._tmpVec2.x || 1920;
                        resolutionY = this._tmpVec2.y || 1080;
                    }
                }
            } catch {
                // Varsayılan değerleri kullan
            }

            this._materyal = new LineMaterial({
                color: renkDeger,
                linewidth: this._ayarlar.kalinlik!,
                transparent: true,
                opacity: this._ayarlar.opaklık!,
                depthTest: !this._ayarlar.enUstte,
                depthWrite: !this._ayarlar.enUstte,
                alphaToCoverage: true,
                resolution: new THREE.Vector2(resolutionX, resolutionY)
            });

            if (this._ayarlar.stil === 'kesikli' || this._ayarlar.stil === 'kesikli-noktali') {
                (this._materyal as LineMaterial).dashed = true;
                (this._materyal as LineMaterial).dashScale = 1;
                (this._materyal as LineMaterial).dashSize = this._ayarlar.kesikliBoyut!;
                (this._materyal as LineMaterial).gapSize = this._ayarlar.kesikliAralik!;
            }
        } else {
            if (this._ayarlar.stil === 'duz') {
                this._materyal = new THREE.LineBasicMaterial({
                    color: renkDeger,
                    transparent: true,
                    opacity: this._ayarlar.opaklık!,
                    depthTest: !this._ayarlar.enUstte,
                    depthWrite: !this._ayarlar.enUstte
                });
            } else {
                this._materyal = new THREE.LineDashedMaterial({
                    color: renkDeger,
                    transparent: true,
                    opacity: this._ayarlar.opaklık!,
                    depthTest: !this._ayarlar.enUstte,
                    depthWrite: !this._ayarlar.enUstte,
                    dashSize: this._ayarlar.kesikliBoyut!,
                    gapSize: this._ayarlar.kesikliAralik!
                });
            }
        }
    }

    /**
     * Çizgi mesh'i oluştur
     */
    private cizgiMeshiOlustur(): void {
        if (!this._geometri || !this._materyal) return;

        // Mevcut çizgiyi temizle
        if (this._cizgi) {
            this.remove(this._cizgi);
            this._cizgi.geometry.dispose();
        }

        if (this._ayarlar.cizgiTipi === 'Line2') {
            this._cizgi = new Line2(
                this._geometri as LineGeometry,
                this._materyal as LineMaterial
            );
            (this._cizgi as Line2).computeLineDistances();
        } else if (this._ayarlar.cizgiTipi === 'LineSegments') {
            this._cizgi = new THREE.LineSegments(
                this._geometri as THREE.BufferGeometry,
                this._materyal as THREE.LineBasicMaterial | THREE.LineDashedMaterial
            );
        } else {
            this._cizgi = new THREE.Line(
                this._geometri as THREE.BufferGeometry,
                this._materyal as THREE.LineBasicMaterial | THREE.LineDashedMaterial
            );
        }

        // UserData
        this._cizgi.userData['elementTipi'] = 'spline';
        this._cizgi.userData['ustElement'] = this;
        this._cizgi.userData['secilebilir'] = this._ayarlar.secilebilir;

        // ciftTiklandi
        const splineRef = this;
        Object.defineProperty(this._cizgi, 'ciftTiklandi', {
            value: function($event?: any) { return splineRef.ciftTiklandi($event); },
            writable: true,
            configurable: true
        });

        // sagMenu
        Object.defineProperty(this._cizgi, 'sagMenu', {
            get: function() { return splineRef.sagMenu; },
            configurable: true
        });

        this.add(this._cizgi);
    }

    // ========================================================================
    // GÜNCELLEME METODLARİ
    // ========================================================================

    /** Son güncelleme sırasındaki nokta sayısı */
    private _sonNoktaSayisi: number = 0;

    /** Curve points cache - getPoints() sonucunu cache'le */
    private _cachedCurvePoints: THREE.Vector3[] = [];
    private _curvePointsDirty: boolean = true;
    private _cachedSegmentSayisi: number = 0;

    /** Pre-allocated positions array for Line2 updates */
    private _positionsBuffer: number[] = [];

    /** Cache'lenmiş curve points döndür */
    private getCachedCurvePoints(): THREE.Vector3[] {
        const segmentSayisi = this._ayarlar.segmentSayisi!;
        if (!this._curvePointsDirty && this._cachedSegmentSayisi === segmentSayisi && this._cachedCurvePoints.length > 0) {
            return this._cachedCurvePoints;
        }
        if (!this._curve) return [];
        this._cachedCurvePoints = this._curve.getPoints(segmentSayisi);
        this._cachedSegmentSayisi = segmentSayisi;
        this._curvePointsDirty = false;
        return this._cachedCurvePoints;
    }

    /** Cache'i dirty olarak isaretle */
    private invalidateCurveCache(): void {
        this._curvePointsDirty = true;
    }

    /**
     * Çizgiyi güncelle
     */
    private cizgiyiGuncelle(): void {
        if (!this._curve) return;

        const noktalar = this.getCachedCurvePoints();

        // Nokta sayısı değiştiyse geometry'yi yeniden oluştur
        if (noktalar.length !== this._sonNoktaSayisi) {
            this._sonNoktaSayisi = noktalar.length;
            this.cizgiyiYenidenOlustur();
            return;
        }

        if (this._ayarlar.cizgiTipi === 'Line2' && this._geometri instanceof LineGeometry) {
            // Pre-allocated positions buffer - boyut degistiyse yeniden olustur
            const requiredLen = noktalar.length * 3;
            if (this._positionsBuffer.length !== requiredLen) {
                this._positionsBuffer = new Array(requiredLen);
            }
            for (let i = 0; i < noktalar.length; i++) {
                this._positionsBuffer[i * 3] = noktalar[i].x;
                this._positionsBuffer[i * 3 + 1] = noktalar[i].y;
                this._positionsBuffer[i * 3 + 2] = noktalar[i].z;
            }
            this._geometri.setPositions(this._positionsBuffer);
            if (this._cizgi instanceof Line2) {
                this._cizgi.computeLineDistances();
            }
        } else if (this._geometri instanceof THREE.BufferGeometry) {
            this._geometri.setFromPoints(noktalar);
        }

        // Handle'ları güncelle
        if (this._handles) {
            this._handles.guncelle();
        }

        // Değişiklik callback'i
        if (this.olaylar?.degisti) {
            this.olaylar.degisti();
        }
    }

    /**
     * Çizgiyi yeniden oluştur
     */
    private cizgiyiYenidenOlustur(): void {
        if (this._cizgi) {
            this.remove(this._cizgi);
            if (this._geometri) this._geometri.dispose();
            if (this._materyal) (this._materyal as THREE.Material).dispose();
        }

        this.cizgiyiOlustur();
    }

    /**
     * Materyal güncelle
     */
    private materyaliGuncelle(): void {
        if (!this._materyal) return;

        const renkDeger = typeof this._ayarlar.renk === 'string'
            ? new THREE.Color(this._ayarlar.renk).getHex()
            : this._ayarlar.renk!;

        if (this._materyal instanceof LineMaterial) {
            this._materyal.color.setHex(renkDeger);
            this._materyal.linewidth = this._ayarlar.kalinlik!;
            this._materyal.opacity = this._ayarlar.opaklık!;
            this._materyal.needsUpdate = true;
        } else {
            (this._materyal as THREE.LineBasicMaterial).color.setHex(renkDeger);
            this._materyal.opacity = this._ayarlar.opaklık!;
            this._materyal.needsUpdate = true;
        }
    }

    /**
     * Stil güncelle
     */
    private stilGuncelle(): void {
        this.cizgiyiYenidenOlustur();
    }

    /**
     * Depth ayarla
     */
    private depthAyarla(): void {
        if (this._materyal) {
            this._materyal.depthTest = !this._ayarlar.enUstte;
            this._materyal.depthWrite = !this._ayarlar.enUstte;
            this._materyal.needsUpdate = true;
        }
    }

    /**
     * Materyali tazele
     */
    materyaliTazele(): void {
        if (!(this._materyal instanceof LineMaterial)) return;

        let resolutionX = 1920;
        let resolutionY = 1080;

        try {
            if (this.sahne?.anaGosterim?.renderer) {
                const renderer = this.sahne.anaGosterim.renderer as unknown as THREE.WebGLRenderer;
                if (renderer.getSize) {
                    renderer.getSize(this._tmpVec2);
                    resolutionX = this._tmpVec2.x || 1920;
                    resolutionY = this._tmpVec2.y || 1080;
                }
            }
        } catch {
            // Varsayılan değerleri kullan
        }

        this._materyal.resolution.set(resolutionX, resolutionY);
        this._materyal.linewidth = this._ayarlar.kalinlik!;
        this._materyal.needsUpdate = true;

        if (this._cizgi instanceof Line2) {
            this._cizgi.computeLineDistances();
        }
    }

    // ========================================================================
    // AYARLAR
    // ========================================================================

    /**
     * Ayarları belirle
     */
    ayarlariBelirle(ayarlar: ISplineAyarlari): void {
        this._ayarlar = { ...VARSAYILAN_AYARLAR, ...ayarlar };
        this.bezierKontrolNoktalariniHesapla();
        this.egriOlustur();
        this.cizgiyiYenidenOlustur();
    }

    /**
     * Mevcut ayarları al
     */
    ayarlariAl(): ISplineAyarlari {
        return { ...this._ayarlar };
    }

    // ========================================================================
    // HANDLE SİSTEMİ
    // ========================================================================

    /**
     * Handle'ları göster
     */
    handlelerGoster(): FikoYT3dSplineHandles {
        if (this._handles) {
            this._handles.guncelle();
        } else {
            this._handles = new FikoYT3dSplineHandles(this);
        }

        if (this.sahne && !this._handles.parent) {
            this.sahne.add(this._handles);
        } else if (!this._handles.parent) {
            this.add(this._handles);
        }

        return this._handles;
    }

    /**
     * Handle'ları gizle
     */
    handlelerGizle(): void {
        if (this._handles) {
            if (this._handles.parent) {
                this._handles.parent.remove(this._handles);
            }
            this._handles.temizle();
            this._handles = null;
        }
    }

    /**
     * Handle referansını al
     */
    get handles(): FikoYT3dSplineHandles | null {
        return this._handles;
    }

    // ========================================================================
    // SAĞ TIKLAMA MENÜSÜ
    // ========================================================================

    /**
     * Sağ tıklama menüsünü oluştur
     */
    private sagMenuOlustur(): void {
        this.sagMenu = [
            {
                simgeYazi: '👁‍🗨',
                baslik: 'Gizle / Göster',
                tiklandi: () => {
                    this.visible = !this.visible;
                }
            },
            {
                simgeYazi: '🖊',
                baslik: 'Düzenle',
                tiklandi: () => this.ciftTiklandi()
            },
            {
                simgeYazi: '➕',
                baslik: 'Nokta Ekle',
                tiklandi: () => {
                    this.egriUzerindeNoktaEkle(0.5);
                }
            },
            {
                tipi: 'Ayraç'
            },
            {
                simgeYazi: this.kapali ? '🔓' : '🔒',
                baslik: 'Kapat/Aç',
                tiklandi: () => {
                    this.kapali = !this.kapali;
                    this.sagMenuOlustur();
                }
            },
            {
                simgeYazi: '🎯',
                baslik: 'Ekrana Sığdır',
                tiklandi: () => this.ekranaSigdir()
            },
            {
                tipi: 'Ayraç'
            },
            {
                simgeYazi: '📋',
                baslik: 'Kopyala',
                tiklandi: () => this.kopyala()
            },
            {
                simgeYazi: '🗑',
                baslik: 'Sil',
                tiklandi: () => this.sil()
            }
        ];
    }

    // ========================================================================
    // ÇIFT TIKLAMA (Dialog aç)
    // ========================================================================

    /** Bu spline için açık dialog referansı */
    private _acikDialog?: any;

    /** Duplicate dialog açmayı önlemek için kilit */
    private _dialogAciliyor = false;

    /** Static: Herhangi bir spline dialog'u açık mı? */
    private static _globalAcikDialog?: any;

    /** Static: Dialog component referansı */
    private static _dialogComponentRef?: WeakRef<any>;

    /** Static: Global dialog'u ayarla */
    static globalDialogAyarla(dialog: any): void {
        FikoYT3dSpline._globalAcikDialog = dialog;
        if (dialog?.pencere?.bilesenInstance) {
            FikoYT3dSpline._dialogComponentRef = new WeakRef(dialog.pencere.bilesenInstance);
        }
    }

    /** Static: Global dialog'u temizle */
    static globalDialogTemizle(): void {
        FikoYT3dSpline._globalAcikDialog = undefined;
        FikoYT3dSpline._dialogComponentRef = undefined;
    }

    /** Static: Global dialog aktif mi */
    static globalDialogAktifMi(): boolean {
        if (FikoYT3dSpline._dialogComponentRef) {
            const componentRef = FikoYT3dSpline._dialogComponentRef.deref();
            if (!componentRef) {
                FikoYT3dSpline.globalDialogTemizle();
                return false;
            }
        }

        if (!FikoYT3dSpline._globalAcikDialog) {
            return false;
        }

        const globalDialog = FikoYT3dSpline._globalAcikDialog;

        if (!globalDialog.gorunur && !globalDialog.miAcik) {
            FikoYT3dSpline.globalDialogTemizle();
            return false;
        }

        return true;
    }

    /**
     * Çift tıklama - Düzenleme dialogunu aç
     */
    async ciftTiklandi($event?: any): Promise<void> {
        if (this._dialogAciliyor) {
            return;
        }

        if (!this.sahne?.anaGosterim?.DialogFiko) {
            return;
        }

        const dialogId = 'fiko-3d-spline-edit-' + this.uuid;
        const dialogService = this.sahne.anaGosterim.DialogFiko;

        if (FikoYT3dSpline.globalDialogAktifMi()) {
            const globalDialog = FikoYT3dSpline._globalAcikDialog;
            console.log('[FikoYT3dSpline] Spline dialog zaten açık');
            if (globalDialog?.id_dialog) {
                this.dialoguOneGetir(globalDialog, dialogService);
            }
            return;
        }

        if (this._acikDialog && this._acikDialog.miAcik) {
            this.dialoguOneGetir(this._acikDialog, dialogService);
            return;
        }

        const mevcutDialog = dialogService.idIleAl?.(dialogId);
        if (mevcutDialog && mevcutDialog.miAcik) {
            this.dialoguOneGetir(mevcutDialog, dialogService);
            this._acikDialog = mevcutDialog;
            return;
        }

        this._acikDialog = undefined;
        this._dialogAciliyor = true;

        try {
            const { FikoYT3dSplineDialogComponent } = await import('./fikoyt-3d-spline-dialog.component');

            const dialog = dialogService.ac({
                bilesen: FikoYT3dSplineDialogComponent,
                ayarlar: { dialogId },
                parametreler: {
                    sahne: this.sahne,
                    mevcutSpline: this,
                    duzenleModu: true
                },
                $event
            });

            this._acikDialog = dialog;
            FikoYT3dSpline._globalAcikDialog = dialog;

        } catch (error) {
            console.error('[FikoYT3dSpline] Dialog import hatası:', error);
        } finally {
            this._dialogAciliyor = false;
        }
    }

    /**
     * Dialog'u öne getir
     */
    private dialoguOneGetir(dialog: any, dialogService: any): void {
        const dialogEl = document.getElementById(dialog.id_dialog);
        if (dialogEl) {
            const yeniZIndex = dialogService.overlayZIndexAl?.() || 99999;
            dialogEl.style.setProperty('z-index', yeniZIndex.toString(), 'important');
            const parent = dialogEl.parentElement;
            if (parent) {
                parent.style.setProperty('z-index', yeniZIndex.toString(), 'important');
            }
            dialogService.dialogTiklandi?.(dialog);
            dialogEl.focus();
        }
    }

    // ========================================================================
    // SERİLEŞTİRME
    // ========================================================================

    /**
     * JSON'a dönüştür
     */
    override toJSON(): any {
        return {
            ...super.toJSON(),
            type: 'FikoYT3dSpline',
            kontrolNoktalar: this._kontrolNoktalar.map(n => ({ x: n.x, y: n.y, z: n.z })),
            bezierKontrolNoktalar: this._bezierKontrolNoktalar.map(seg =>
                seg.map(n => ({ x: n.x, y: n.y, z: n.z }))
            ),
            ayarlar: { ...this._ayarlar }
        };
    }

    /**
     * JSON'dan oluştur
     */
    static fromJSON(json: any, sahne?: FikoSahne): FikoYT3dSpline {
        const spline = new FikoYT3dSpline(sahne, json.name);

        if (json.kontrolNoktalar) {
            spline._kontrolNoktalar = json.kontrolNoktalar.map((n: any) =>
                new THREE.Vector3(n.x, n.y, n.z)
            );
        }

        if (json.bezierKontrolNoktalar) {
            spline._bezierKontrolNoktalar = json.bezierKontrolNoktalar.map((seg: any[]) =>
                seg.map((n: any) => new THREE.Vector3(n.x, n.y, n.z))
            );
        }

        if (json.ayarlar) {
            spline._ayarlar = { ...VARSAYILAN_AYARLAR, ...json.ayarlar };
        }

        spline.egriOlustur();
        spline.cizgiyiYenidenOlustur();
        return spline;
    }

    // ========================================================================
    // YAŞAM DÖNGÜSÜ
    // ========================================================================

    /**
     * Her frame güncelleme
     */
    override guncelle(deltaTime?: number, kamera?: THREE.Camera): void {
        super.guncelle(deltaTime, kamera);

        if (this._materyal instanceof LineMaterial && this.sahne?.anaGosterim?.renderer) {
            const renderer = this.sahne.anaGosterim.renderer as unknown as THREE.WebGLRenderer;
            if (renderer.getSize) {
                renderer.getSize(this._tmpVec2);
                this._materyal.resolution.set(this._tmpVec2.x, this._tmpVec2.y);
            }
        }
    }

    /**
     * Spline'ı ekrana sığdır
     */
    ekranaSigdir(): void {
        const anaGosterim = this.sahne?.anaGosterim as any;
        if (!anaGosterim) {
            console.warn('[FikoYT3dSpline] Ekrana sığdırma için anaGosterim bulunamadı');
            return;
        }

        const box = this._tmpBox3;
        box.makeEmpty();
        for (let i = 0; i < this._kontrolNoktalar.length; i++) {
            box.expandByPoint(this._kontrolNoktalar[i]);
        }

        const size = this._tmpVec3A;
        box.getSize(size);
        const padding = Math.max(size.length() * 0.5, 50);
        box.expandByScalar(padding);

        const center = this._tmpVec3B;
        box.getCenter(center);

        const sizeAfter = this._tmpVec3C;
        box.getSize(sizeAfter);
        const radius = sizeAfter.length() / 2;

        const kamera = anaGosterim.aktifKamera;
        const kontrol = anaGosterim.aktifKontrol;

        if (!kamera || !kontrol) {
            console.warn('[FikoYT3dSpline] Kamera veya kontrol bulunamadı');
            return;
        }

        const canvas = anaGosterim.canvas?.nativeElement;
        if (!canvas || canvas.clientWidth === 0) {
            console.warn('[FikoYT3dSpline] Canvas bulunamadı');
            return;
        }

        const aspect = canvas.clientWidth / canvas.clientHeight;

        if (typeof TWEEN !== 'undefined') {
            anaGosterim.surekliCiz?.();

            const yakinlik = (aspect > 1.0)
                ? canvas.clientHeight / (radius * 2)
                : canvas.clientWidth / (radius * 2);

            const oncekiTarget = this._tmpVec3D;
            oncekiTarget.copy(kontrol.target);
            // kameraOffset ve kameraYeniPoz hesapla - TWEEN scalar değerler kullanacağı için
            // sadece hedef değerleri hesaplayıp tween'e ver
            const kameraOffsetX = kamera.position.x - oncekiTarget.x;
            const kameraOffsetY = kamera.position.y - oncekiTarget.y;
            const kameraOffsetZ = kamera.position.z - oncekiTarget.z;
            const kameraYeniPozX = center.x + kameraOffsetX;
            const kameraYeniPozY = center.y + kameraOffsetY;
            const kameraYeniPozZ = center.z + kameraOffsetZ;

            new TWEEN.Tween({
                x: kamera.position.x,
                y: kamera.position.y,
                z: kamera.position.z,
                zoom: kamera.zoom
            })
            .to({
                x: kameraYeniPozX,
                y: kameraYeniPozY,
                z: kameraYeniPozZ,
                zoom: yakinlik
            }, 400)
            .easing(TWEEN.Easing.Quadratic.Out)
            .onUpdate((val: any) => {
                kamera.position.set(val.x, val.y, val.z);
                kamera.zoom = val.zoom;
                kamera.updateProjectionMatrix();
            })
            .onComplete(() => {
                anaGosterim.surekliCizIptal?.();
            })
            .start();

            new TWEEN.Tween({
                x: kontrol.target.x,
                y: kontrol.target.y,
                z: kontrol.target.z
            })
            .to({
                x: center.x,
                y: center.y,
                z: center.z
            }, 400)
            .easing(TWEEN.Easing.Quadratic.Out)
            .onUpdate((val: any) => {
                kontrol.target.set(val.x, val.y, val.z);
                kontrol.update();
            })
            .start();

        } else {
            kontrol.target.copy(center);
            kamera.lookAt(center);
            kontrol.update();
        }
    }

    /**
     * Kopyala
     */
    kopyala(): FikoYT3dSpline {
        const kopya = new FikoYT3dSpline(this.sahne, this.name + '_kopya');
        kopya._kontrolNoktalar = this._kontrolNoktalar.map(n => n.clone());
        kopya._bezierKontrolNoktalar = this._bezierKontrolNoktalar.map(seg => seg.map(n => n.clone()));
        kopya._ayarlar = { ...this._ayarlar };
        kopya.egriOlustur();
        kopya.cizgiyiYenidenOlustur();

        if (this.sahne) {
            this.sahne.add(kopya);
        }

        return kopya;
    }

    /**
     * Sil
     */
    sil(): void {
        this.handlelerGizle();
        this.temizle();

        if (this.parent) {
            this.parent.remove(this);
        }
    }

    /**
     * Kaynakları temizle
     */
    override temizle(): void {
        this.handlelerGizle();

        if (this._cizgi) {
            this.remove(this._cizgi);
            if (this._geometri) this._geometri.dispose();
            if (this._materyal) (this._materyal as THREE.Material).dispose();
        }

        super.temizle();
    }
}

// ============================================================================
// YARDIMCI FONKSİYONLAR
// ============================================================================

/**
 * Spline uzunluğu hesapla
 */
export function splineUzunlugu(curve: THREE.Curve<THREE.Vector3>): number {
    return curve.getLength();
}

/**
 * Spline merkezi hesapla
 */
export function splineMerkezi(noktalar: THREE.Vector3[]): THREE.Vector3 {
    const merkez = new THREE.Vector3();
    noktalar.forEach(n => merkez.add(n));
    merkez.divideScalar(noktalar.length);
    return merkez;
}
